<template>
  <main v-if="post" class="min-h-screen bg-[#f7f5ef] text-stone-950">
    <article>
      <header class="border-b border-stone-200 bg-white">
        <div class="mx-auto max-w-4xl px-5 py-12 md:px-8 md:py-16">
          <NuxtLink class="text-sm font-black text-teal-800 hover:text-teal-950" to="/blog">← All articles</NuxtLink>
          <p class="mt-8 text-xs font-black uppercase tracking-[0.2em] text-teal-700">{{ post.eyebrow }}</p>
          <h1 class="mt-4 text-4xl font-black leading-[1.08] tracking-tight sm:text-6xl">{{ post.title }}</h1>
          <p class="mt-6 text-xl leading-9 text-stone-700">{{ post.description }}</p>
          <div class="mt-6 flex flex-wrap gap-3 text-sm font-bold text-stone-500">
            <span>{{ formatDate(post.publishedAt) }}</span><span>·</span><span>{{ post.readingMinutes }} min read</span><span>·</span><span>{{ post.city }}</span>
          </div>
        </div>
      </header>

      <div class="mx-auto max-w-5xl px-5 py-8 md:px-8">
        <img :src="post.heroImage" :alt="post.heroAlt" class="max-h-[560px] w-full rounded-2xl object-cover shadow-sm" />
      </div>

      <div class="mx-auto grid max-w-5xl gap-10 px-5 pb-16 md:px-8 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div class="space-y-10">
          <section v-for="section in post.sections" :key="section.heading">
            <h2 class="text-3xl font-black tracking-tight">{{ section.heading }}</h2>
            <div class="mt-4 space-y-5 text-lg leading-8 text-stone-750">
              <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
            </div>
          </section>

          <section class="rounded-xl bg-stone-950 p-6 text-white">
            <h2 class="text-2xl font-black">What to carry into your own quote request</h2>
            <ul class="mt-4 space-y-3 text-stone-200">
              <li v-for="takeaway in post.takeaways" :key="takeaway" class="flex gap-3"><span class="text-teal-400">✓</span><span>{{ takeaway }}</span></li>
            </ul>
          </section>

          <section>
            <p class="text-xs font-black uppercase tracking-[0.18em] text-teal-700">Questions answered</p>
            <h2 class="mt-3 text-3xl font-black tracking-tight">Common questions about this topic</h2>
            <div class="mt-5 divide-y divide-stone-200 rounded-xl border border-stone-300 bg-white px-5">
              <details v-for="faq in post.faqs" :key="faq.question" class="group py-5">
                <summary class="cursor-pointer list-none pr-4 font-black">{{ faq.question }}</summary>
                <p class="mt-3 leading-7 text-stone-700">{{ faq.answer }}</p>
              </details>
            </div>
          </section>
        </div>

        <aside class="space-y-5 lg:sticky lg:top-24 lg:h-fit">
          <section v-if="post.companyName" class="rounded-xl border border-stone-300 bg-white p-5 shadow-sm">
            <p class="text-xs font-black uppercase tracking-[0.16em] text-stone-500">Company in this story</p>
            <h2 class="mt-2 text-xl font-black">{{ post.companyName }}</h2>
            <p class="mt-2 text-sm leading-6 text-stone-600">{{ post.companyBlurb }}</p>
            <NuxtLink v-if="post.cleanerSlug" :to="`/cleaners/${post.cleanerSlug}`" class="mt-4 inline-flex text-sm font-black text-teal-800 underline">View ClearFlow profile →</NuxtLink>
            <a v-if="post.companyWebsite" :href="post.companyWebsite" class="mt-4 inline-flex text-sm font-black text-teal-800 underline" target="_blank" rel="noopener noreferrer">Visit company website ↗</a>
            <NuxtLink class="mt-4 inline-flex text-sm font-black text-teal-800 underline" to="/blog/what-does-gutter-cleaning-include-los-angeles">Use our gutter-cleaning scope checklist</NuxtLink>
            <QuicklistToggle v-if="post.cleanerId" class="mt-4 w-full" :cleaner-id="post.cleanerId" :company-name="post.companyName" />
          </section>
          <section v-else-if="post.slug === 'el-nino-los-angeles-winter-home-preparation'" class="rounded-xl border border-stone-300 bg-white p-5 shadow-sm">
            <p class="text-xs font-black uppercase tracking-[0.16em] text-stone-500">About this guide</p>
            <h2 class="mt-2 text-xl font-black">A composite Los Angeles homeowner</h2>
            <p class="mt-2 text-sm leading-6 text-stone-600">Elena is a composite example used to connect official weather guidance with practical home preparation. She is not presented as a real customer or testimonial.</p>
          </section>
          <section class="rounded-xl bg-teal-800 p-5 text-white">
            <h2 class="text-xl font-black">Have a similar problem?</h2>
            <p class="mt-2 text-sm leading-6 text-teal-50">Compare local cleaners and save the best fits to your Quicklist.</p>
            <NuxtLink class="mt-4 inline-flex rounded-md bg-white px-4 py-3 text-sm font-black text-teal-900" :to="`/cleaners?city=${encodeURIComponent(post.city.split(',')[0])}`">Find local cleaners</NuxtLink>
          </section>
          <p class="text-xs leading-5 text-stone-500">
            Source: <a :href="post.sourceUrl" class="underline" target="_blank" rel="noopener noreferrer">{{ post.sourceLabel }}</a>.<span v-if="post.companyName"> ClearFlow did not independently inspect this job.</span><span v-else-if="post.slug === 'el-nino-los-angeles-winter-home-preparation'"> Seasonal outlooks can change; check current official forecasts before acting.</span><span v-else> Service scopes vary by company and property. Confirm the written estimate before booking.</span>
          </p>
        </aside>
      </div>
    </article>
  </main>
</template>

<script setup lang="ts">
import { getBlogPostBySlug } from "~~/data/blogPosts";

const route = useRoute();
const post = computed(() => getBlogPostBySlug(String(route.params.slug || "")));

if (!post.value) throw createError({ statusCode: 404, statusMessage: "Blog post not found" });

function formatDate(date: string) {
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(`${date}T12:00:00`));
}

const canonicalUrl = computed(() => `https://www.clearflowgutters.pro/blog/${post.value?.slug || ""}`);

useHead({
  title: computed(() => `${post.value?.title} | ClearFlow`),
  link: [{ rel: "canonical", href: canonicalUrl }],
  meta: [
    { name: "description", content: computed(() => post.value?.description || "") },
    { property: "og:title", content: computed(() => post.value?.title || "") },
    { property: "og:description", content: computed(() => post.value?.description || "") },
    { property: "og:type", content: "article" },
    { property: "og:url", content: canonicalUrl },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: computed(() => JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.value?.title,
        description: post.value?.description,
        datePublished: post.value?.publishedAt,
        dateModified: post.value?.updatedAt,
        image: `https://www.clearflowgutters.pro${post.value?.heroImage}`,
        mainEntityOfPage: canonicalUrl.value,
        author: { "@type": "Organization", name: "ClearFlow Gutter Services" },
        publisher: { "@type": "Organization", name: "ClearFlow Gutter Services" },
      })),
    },
    {
      type: "application/ld+json",
      innerHTML: computed(() => JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.value?.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      })),
    },
  ],
});
</script>
